<script lang="ts">
  import { onMount } from 'svelte';
  import { fade } from 'svelte/transition';
  import ArrowUp from '@lucide/svelte/icons/arrow-up';
  import { Button } from '../../base/button';
  import { cn } from '../../tools';
  import { SCROLL_TO_TOP_HIDE_RATIO, SCROLL_TO_TOP_SHOW_RATIO } from './constants';
  let {
    label,
    target = null,
    clearance = 'default',
    class: className,
    forceVisible = false
  }: {
    label: string;
    target?: HTMLElement | null;
    clearance?: 'default' | 'mobile-nav' | 'ask-ai';
    class?: string;
    forceVisible?: boolean;
  } = $props();
  let visible = $state(false);
  let reducedMotion = $state(false);
  function updateVisibility() {
    const container = target ?? document.documentElement;
    const viewportHeight = target?.clientHeight ?? window.innerHeight;
    const scrollTop = target?.scrollTop ?? window.scrollY;
    if (container.scrollHeight <= viewportHeight || scrollTop < viewportHeight * SCROLL_TO_TOP_HIDE_RATIO) {
      visible = false;
      return;
    }

    if (scrollTop >= viewportHeight * SCROLL_TO_TOP_SHOW_RATIO) visible = true;
  }
  function scrollToTop() {
    const container = target ?? window;
    const behavior = window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth';
    container.scrollTo({ top: 0, behavior });
  }
  onMount(() => {
    const preference = window.matchMedia('(prefers-reduced-motion: reduce)');
    reducedMotion = preference.matches;
    const updatePreference = () => {
      reducedMotion = preference.matches;
    };
    preference.addEventListener('change', updatePreference);
    return () => preference.removeEventListener('change', updatePreference);
  });
  $effect(() => {
    const container = target ?? document.body;
    const scrollTarget = target;
    const resizeObserver = new ResizeObserver(updateVisibility);
    resizeObserver.observe(container);
    const mutationObserver = new MutationObserver(updateVisibility);
    mutationObserver.observe(container, { childList: true, subtree: true });
    scrollTarget?.addEventListener('scroll', updateVisibility, { passive: true });
    queueMicrotask(updateVisibility);
    return () => {
      resizeObserver.disconnect();
      mutationObserver.disconnect();
      scrollTarget?.removeEventListener('scroll', updateVisibility);
    };
  });
</script>

<svelte:window onscroll={updateVisibility} onresize={updateVisibility} />
{#if visible || forceVisible}
  <div
    transition:fade={{ duration: reducedMotion ? 0 : 120 }}
    class={cn(
      'ui:fixed ui:right-6 ui:bottom-[max(1.5rem,env(safe-area-inset-bottom)+1rem)] ui:z-30',
      clearance !== 'default' && 'ui:max-lg:bottom-24',
      className
    )}
  >
    <Button
      type="button"
      variant="secondary"
      size="icon"
      class="ui:rounded-full ui:shadow-md"
      aria-label={label}
      title={label}
      onclick={scrollToTop}><ArrowUp class="ui:size-5" /></Button
    >
  </div>
{/if}
